import { DestroyRef, EffectRef, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, fromEvent } from 'rxjs';

/**
 * Closes a side panel when the user presses Escape. Must be called in an
 * injection context (e.g. a field initializer of the panel component).
 *
 * Keydowns already consumed by an overlay (dialogs, menus and selects call
 * `preventDefault()` on Escape) are ignored, so Escape dismisses only the
 * topmost layer instead of also closing the panel underneath.
 *
 * Returns the EffectRef so the panel can destroy it once closing has started.
 */
export function closePanelOnEscape(close: () => unknown): EffectRef {
  const destroyRef = inject(DestroyRef);
  let destroyed = false;
  destroyRef.onDestroy(() => (destroyed = true));

  const escapePressed = toSignal(
    fromEvent<KeyboardEvent>(document, 'keydown').pipe(
      filter((event) => event.key === 'Escape' && !event.defaultPrevented)
    )
  );

  return effect(() => {
    if (!escapePressed()) return;
    // Deferred: closing detaches the panel from the portal, and destroying the view
    // while its effects are being flushed crashes change detection (angular/angular#62822).
    setTimeout(() => {
      if (!destroyed) close();
    });
  });
}
